/**
 * SourceListVertical Component
 * Displays sources in a vertical panel with header and optional footer content
 * Similar to online-community's SourceListVertical
 */

import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { Icon, Tooltip } from '@hl/ui-components';
import React, { ReactNode, useEffect, useRef } from 'react';

import { MessageReference } from '../../../Message/types/messages';
import SourceCard from './SourceCard';
import {
  SourceListContainer,
  SourceListHeader,
  SourceListScrollContainer,
} from './SourceListVertical.styled';

export interface SourceListVerticalProps {
  sources: MessageReference[];
  className?: string;
  footer?: ReactNode;
}

/**
 * SourceListVertical Component
 * Renders a vertical list of sources with optional footer content
 */
const SourceListVertical: React.FC<SourceListVerticalProps> = ({
  sources,
  className = '',
  footer,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to top when sources change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [sources]);

  const tooltipText = 
    "AI messages reference content within this site that you have access to view. External web content is not available to AI. However, it can use the LLM's training data when no relevant community content is available.";

  return (
    <SourceListContainer className={className}>
      <SourceListHeader>
        {sources.length} {sources.length === 1 ? 'source' : 'sources'}
        {' '}
        <Tooltip overlay={tooltipText} placement="bottom">
          <span style={{ cursor: 'help', marginLeft: '4px' }}>
            <Icon icon={faInfoCircle} />
          </span>
        </Tooltip>
      </SourceListHeader>
      <SourceListScrollContainer ref={scrollContainerRef}>
        {sources.map((source, index) => (
          <SourceCard
            key={`source-${index}`}
            source={source}
            ordinal={index + 1}
          />
        ))}
      </SourceListScrollContainer>
      {footer}
    </SourceListContainer>
  );
};

export default SourceListVertical;
